import {
  borderRadius,
  colors,
  fontWeights,
  getFontStyle,
  getSpacing,
  spacing,
} from '@cabonline/ui-shared';
import styled from '@emotion/styled';
import { GroupBase, StylesConfig, ThemeConfig } from 'react-select';

import { CustomStyles } from './common';

export const labelHeight = `${spacing.m}px`;

export const Container = styled.div`
  width: auto;
`;

export const ErrorWrap = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  height: ${labelHeight};
  margin-bottom: 4px;
`;

export const ErrorText = styled.div`
  ${getFontStyle('s', 'semi')};
  color: ${colors.red};
  padding-left: 4px;
`;

export const StyledValueWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: ${getSpacing('xxs')};
  padding-top: ${getSpacing('xxxs')};
`;

export const StyledIconWrapper = styled.div`
  display: flex;
  align-items: center;
`;

export const StyledSelectMenuHeader = styled.div`
  display: flex;
  align-items: center;
  border-bottom: 1px solid ${colors.gray3};
  padding: ${getSpacing('xxxs')} ${getSpacing('xxs')};
  gap: ${getSpacing('xxxs')};
`;

export const StyledSelectSearchInput = styled.input`
  flex: 1;
  border: none;
  outline: none;
  ${getFontStyle('s', 'semi')};
  color: ${colors.black};
  background: transparent;
  padding: ${getSpacing('xxxs')} 0;
  &::placeholder {
    color: ${colors.gray2};
  }
`;

export const getStyles = <
  Option,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>
>(
  hasError?: boolean | string,
  darkMode?: boolean,
  customStyles?: CustomStyles
): StylesConfig<Option, IsMulti, Group> => ({
  control: (base, state) => ({
    ...base,
    fontFamily: '"Plus Jakarta Sans", sans-serif',
    borderColor: getBorderColor(!!hasError, state.isFocused, !!darkMode),
    backgroundColor: getBackgroundColor(!!darkMode, state.isDisabled),
    height: 48,
    minHeight: 48,
    ':hover': {
      ...base[':hover'],
      borderColor: colors.black,
      backgroundColor: darkMode ? colors.gray1 : colors.gray4,
    },
    ...customStyles?.control,
  }),
  option: (base, state) => ({
    ...base,
    fontFamily: '"Plus Jakarta Sans", sans-serif',
    backgroundColor: getOptionBackgroundColor(
      state.isSelected,
      state.isFocused
    ),
    fontWeight: state.isSelected ? fontWeights.bold : fontWeights.semi,
    color: colors.black,
    ':active': {
      backgroundColor: state.isSelected ? colors.primaryHover : colors.gray3,
    },
    ...customStyles?.option,
  }),
  singleValue: (base) => ({
    ...base,
    color: darkMode ? colors.white : colors.black,
    ...customStyles?.singleValue,
  }),
  dropdownIndicator: (base) => ({
    ...base,
    color: darkMode ? colors.white : colors.black,
    ':hover': {
      color: darkMode ? colors.white : colors.black,
    },
  }),
  menu: (base) => ({
    ...base,
    ...customStyles?.menu,
  }),
  menuList: (base) => ({
    ...base,
    ...customStyles?.menuList,
  }),
});

export const getThemeConfig = (): ThemeConfig => (theme) => ({
  ...theme,
  borderRadius: borderRadius.s,
  height: 48,
  colors: {
    ...theme.colors,
    primary25: colors.gray4,
    primary: colors.black,
  },
});

const getBorderColor = (
  hasError: boolean,
  isFocused: boolean,
  isDarkMode: boolean
) => {
  if (hasError) {
    return colors.red;
  } else if (isFocused && isDarkMode) {
    return colors.white;
  }
  return colors.gray2;
};

const getBackgroundColor = (isDarkMode: boolean, isDisabled: boolean) => {
  if (isDisabled) {
    return colors.gray3;
  }
  return isDarkMode ? 'transparent' : colors.white;
};

const getOptionBackgroundColor = (isSelected: boolean, isFocused: boolean) => {
  if (isSelected) {
    return isFocused ? colors.primaryHover : colors.primary;
  } else {
    return isFocused ? colors.gray4 : 'transparent';
  }
};
